import { useMemo, useState } from "react";
import { getBrowserTimezone } from "@visioni/a3s-vehicle-tracker/app";
import type { CameraAssetGroup } from "@visioni/a3s-vehicle-tracker";

interface TimezoneSelectorResult {
  options: string[];
  needsSelector: boolean;
  activeTimezone: string;
  setActiveTimezone: (tz: string | null) => void;
}

/**
 * Timezone selector for the vehicle tracker.
 * See docs/vehicle-tracker-timezones.md for the full decision record.
 */
export function useTimezoneSelector(cameras: CameraAssetGroup[] | undefined): TimezoneSelectorResult {
  const browserTz = useMemo(() => getBrowserTimezone(), []);

  const siteTimezones = useMemo(() => {
    if (!cameras?.length) return [];
    const tzSet = new Set(cameras.map((c) => c.siteTimezone).filter(Boolean) as string[]);
    return [...tzSet].sort();
  }, [cameras]);

  const options = useMemo(() => {
    const set = new Set([browserTz, ...siteTimezones]);
    return [...set].sort();
  }, [browserTz, siteTimezones]);

  const needsSelector = siteTimezones.length > 1;

  const [selected, setSelected] = useState<string | null>(null);

  const activeTimezone = useMemo(() => {
    if (selected && options.includes(selected)) return selected;
    if (siteTimezones.length >= 1) return siteTimezones[0]!;
    return browserTz;
  }, [selected, options, siteTimezones, browserTz]);

  return { options, needsSelector, activeTimezone, setActiveTimezone: setSelected };
}
